"use client";

import { ComponentProps, FC, ReactNode, useEffect } from "react";
import { Pill } from "../Pill";
import { XMarkThick, XMarkThin } from "@/icons";
import { Button } from "../Button";
import { Analytics } from "@/types";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import { useFilters } from "@/hooks";
import { Filters } from "@/types/General";
import { getLastCategory } from "@/helpers";
import cn from "classnames";

const renderCountText = (template: string, count: number): ReactNode => {
  const [prefix, ...rest] = template.split("{COUNT}");
  if (rest.length === 0) {
    return template;
  }

  return (
    <>
      {prefix}
      <span className="font-bold">{count}</span>
      {rest.join(" ")}
    </>
  );
};

type FilterPanelProps = {
  selectedFilters: Filters;
  isLoading: boolean;
  onFilter: (filters: Filters) => void;
  clearAll: { text: string } & Analytics;
  numberOfResults: number;
  noResultsFoundText: string;
  resultsFoundText: string;
  singularResultFoundText: string;
  loadingText: string;
  withBottomLine?: boolean;
  showPillsFirst?: boolean;
} & ComponentProps<"div">;
export const FilterPanel: FC<FilterPanelProps> = ({
  selectedFilters,
  clearAll,
  numberOfResults,
  noResultsFoundText,
  resultsFoundText,
  singularResultFoundText,
  isLoading,
  loadingText,
  withBottomLine = false,
  showPillsFirst = true,
  onFilter,
  ...props
}) => {
  const {
    selectedFilters: internalSelectedFilters,
    onDeselectFilters,
    onClearAll,
    setSelectedFilters,
  } = useFilters(selectedFilters);

  useEffect(() => {
    setSelectedFilters(selectedFilters);
  }, [selectedFilters, setSelectedFilters]);

  const pills = internalSelectedFilters.flatMap(({ name, paths }) =>
    paths.map((category) => ({
      path: category,
      label: getLastCategory(category).replace(/\s\(\+?\d+\)$/g, ""), // replace count in labels (CSC search)
      name,
    })),
  );

  const resultsLabel = isLoading
    ? loadingText
    : numberOfResults > 0
      ? renderCountText(
          numberOfResults === 1 ? singularResultFoundText : resultsFoundText,
          numberOfResults,
        )
      : noResultsFoundText;

  return (
    <div {...props}>
      <div
        className={cn(
          "flex flex-col",
          withBottomLine && "border-b-symbol-gray-100-graphic border-b-1",
          showPillsFirst ? "gap-4" : "gap-8 lg:gap-6",
        )}
      >
        {pills.length ? (
          <div
            className={cn(
              "flex flex-row items-center justify-between gap-x-4",
              showPillsFirst ? "order-1" : "order-2",
            )}
          >
            <div className="flex flex-row flex-wrap gap-2.5">
              {pills.sort().map(({ name, label, path }) => (
                <Pill
                  data-automation-group="selected-filter"
                  data-linktype="Filter Component"
                  data-linkdetail={`Filter Component Remove ${label}`}
                  aria-label={`Remove ${label} filter`}
                  key={path}
                  className="group flex flex-row items-center gap-1"
                  onClick={() => {
                    const newFilters = onDeselectFilters(name, [path]);
                    onFilter(newFilters);
                  }}
                >
                  {label}
                  <XMarkThin
                    width={20}
                    height={20}
                    className="group-hover:hidden"
                    dataAutomationId="filtering-pill-remove-icon"
                    fill="#444"
                    aria-hidden
                  />
                  <XMarkThick
                    width={20}
                    height={20}
                    className="hidden group-hover:block"
                    dataAutomationId="filtering-pill-remove-icon"
                    fill="#444"
                    aria-hidden
                  />
                </Pill>
              ))}
            </div>
            <Button
              className="shrink-0 text-right capitalize underline hover:no-underline!"
              variant="tertiary"
              icon="none"
              size="xs"
              onClick={() => {
                const newFilters = onClearAll();
                onFilter(newFilters);
              }}
              {...getDataLinkAttributes(clearAll)}
              {...getDataAutomationId("clear-all-button")}
            >
              {clearAll.text}
            </Button>
          </div>
        ) : null}
        <span className="text-body-s text-headline-eyebrow">
          {resultsLabel}
        </span>
      </div>
    </div>
  );
};
